import { useEffect } from 'react';
import { useAuth } from './useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import logger from '../utils/logger';

/**
 * Simple auth navigation hook
 * Redirects to login if not authenticated, or to feed if authenticated
 */
export const useAuthNavigation = () => {
  const { user, isInitialized } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isInitialized) {
      return; // Wait for auth to initialize
    }

    const currentPath = location.pathname;
    const isAuthPage = currentPath.includes('/auth/');
    const isWelcomePage = currentPath === '/welcome';
    const isClearMatPage = currentPath.startsWith('/clearmat');
    const isLegalPage = currentPath === '/privacy' || currentPath === '/terms';
    // ClearMat and legal pages are accessible by both authenticated and unauthenticated users
    const isPublicPage = isAuthPage || isWelcomePage || isClearMatPage || isLegalPage;

    if (user) {
      // User is authenticated
      // Only redirect from auth/welcome pages, NOT from ClearMat (which is publicly accessible to all)
      if (isAuthPage || isWelcomePage) {
        logger.info('User authenticated on auth/welcome page, redirecting to feed');
        navigate('/feed', { replace: true });
      }
    } else {
      // User is not authenticated
      if (!isPublicPage) {
        // Check if user has seen welcome screen (localStorage)
        const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');

        if (hasSeenWelcome) {
          // Go directly to login
          logger.info('User not authenticated, redirecting to login');
          navigate('/auth/login', { replace: true });
        } else {
          // Show welcome slides first
          logger.info('User not authenticated, showing welcome screen');
          navigate('/welcome', { replace: true });
        }
      }
    }
  }, [user, isInitialized, location.pathname, navigate]);
};

export default useAuthNavigation;